import React, { useState } from 'react';
import { Alert, Button, Modal, OverlayTrigger, Spinner, StatusBadge, Table, TableScroll, Tooltip, SortableTh } from '@/components/ui';
import {
  type PcrGroupMember,
  type PcrGroupedResult,
  type PcrSimilarityGroup,
  rejectPcrGroupSuggestion,
  formatPcrStatusLabel,
  normalizePcrReportTypeBucket,
} from '../../../services/pcrService';
import { sortRowsBy } from '../../../utils/useClientSort';

// Sort is applied to the members WITHIN each similarity group (the grouping stays intact).
const MEMBER_SORT_VALUES: Record<string, (m: PcrGroupMember) => string | number | null | undefined> = {
  reportId: (m) => m.reportId ?? m.id,
  receivedAt: (m) => (m.receivedAt ? new Date(m.receivedAt).getTime() : null),
  status: (m) => m.status,
  sla: (m) => m.responseTimeBucket,
  foremanArea: (m) => m.foremanAreaName ?? m.community ?? m.serviceArea,
  activity: (m) => m.activityType ?? m.reportType,
  similarity: (m) => m.similarityScore,
};
import type { ToastType } from '../../../contexts/ToastContext';
import PcrBulkActionsToolbar from './PcrBulkActionsToolbar';

/** Strip the "(details…)" parenthetical from activity type for compact display. */
function shortActivity(value: string | null | undefined): string {
  if (!value) return '–';
  const idx = value.indexOf('(');
  return idx > 0 ? value.slice(0, idx).trim() : value.trim();
}

function formatReceived(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function SimilarityBar({ score }: { score: number | null }) {
  if (score == null) return <span className="tw-text-content-muted">–</span>;
  const pct = Math.round(score * 100);
  return (
    <div className="tw-flex tw-items-center tw-gap-1">
      <div
        style={{
          height: 4,
          width: 56,
          background: '#d0d7de',
          borderRadius: 2,
          overflow: 'hidden',
          flexShrink: 0,
        }}
      >
        <div
          style={{ height: '100%', width: `${pct}%`, background: '#0d6efd', borderRadius: 2 }}
        />
      </div>
      <span style={{ fontSize: '0.8rem', color: '#555' }}>{pct}%</span>
    </div>
  );
}

const COL_COUNT = 8; // checkbox/chevron | PCR ID | Received | Status | SLA | Foreman area | Activity | Similarity

export interface PcrGroupedTableProps {
  result: PcrGroupedResult | null;
  loading: boolean;
  error: string | null;
  onDismissError?: () => void;
  onOpenDetail: (id: number) => void;
  onOpenLinkedWorkId?: (linkedWorkIdentificationId: string) => void;
  onGroupChange: () => void;
  onBulkSuccess: (message: string, toastType?: ToastType) => void;
  canClose?: boolean;
  canAddNote?: boolean;
  canLinkWorkId?: boolean;
}

const PcrGroupedTable: React.FC<PcrGroupedTableProps> = ({
  result,
  loading,
  error,
  onDismissError,
  onOpenDetail,
  onGroupChange,
  onBulkSuccess,
  canClose = false,
  canAddNote = false,
  canLinkWorkId = false,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [removingKey, setRemovingKey] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [showSimilarityInfo, setShowSimilarityInfo] = useState(false);
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (key: string, dir: 'asc' | 'desc') => {
    setSortBy(key);
    setSortDir(dir);
  };

  /** Members ordered by the active sort; falls back to the server's order. */
  const sortMembers = (members: PcrGroupMember[]): PcrGroupMember[] => {
    const getVal = sortBy ? MEMBER_SORT_VALUES[sortBy] : undefined;
    return getVal ? sortRowsBy(members, getVal, sortDir) : members;
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroupSelect = (group: PcrSimilarityGroup) => {
    const ids = group.members.map((m) => m.id);
    const allSelected = ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleCollapse = (groupKey: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const handleRemoveFromGroup = async (memberId: number, group: PcrSimilarityGroup) => {
    const otherIds = group.members.filter((m) => m.id !== memberId).map((m) => m.id);
    if (otherIds.length === 0) return;
    const key = `${memberId}_${group.groupKey}`;
    setRemovingKey(key);
    setRemoveError(null);
    try {
      await Promise.all(otherIds.map((otherId) => rejectPcrGroupSuggestion(memberId, otherId)));
      onGroupChange();
    } catch (e) {
      setRemoveError(e instanceof Error ? e.message : 'Failed to remove from group.');
    } finally {
      setRemovingKey(null);
    }
  };

  if (error) {
    return (
      <Alert variant="danger" className="tw-m-3" dismissible={!!onDismissError} onClose={onDismissError}>
        {error}
      </Alert>
    );
  }

  if (loading && !result) {
    return (
      <div className="tw-flex tw-justify-center tw-p-5">
        <Spinner animation="border" role="status">
          <span className="tw-sr-only">Loading…</span>
        </Spinner>
      </div>
    );
  }

  if (!result || (result.groups.length === 0 && result.ungrouped.length === 0)) {
    return (
      <div className="tw-text-center tw-text-content-muted tw-py-5">
        <i className="bi bi-collection tw-mr-2" style={{ fontSize: '1.5rem' }} />
        <p className="tw-mb-1 tw-font-semibold">No PCR submissions found.</p>
        <p className="tw-text-sm">Adjust filters or use "Regenerate" to compute groupings.</p>
      </div>
    );
  }

  const totalMembers =
    result.groups.reduce((n, g) => n + g.members.length, 0) + result.ungrouped.length;

  return (
    // Fills ListPage.Body: the bulk toolbar and the status bar keep their height, the table box
    // between them is the one scroll area (sticky header attaches to it).
    <div className="tw-flex tw-min-h-0 tw-min-w-0 tw-flex-1 tw-flex-col">
      {removeError && (
        <Alert variant="danger" dismissible onClose={() => setRemoveError(null)} className="tw-mb-2 tw-shrink-0">
          {removeError}
        </Alert>
      )}

      {/* Sticky top bulk actions toolbar */}
      <div
        className="tw-flex tw-items-center tw-shrink-0"
        style={{
          background: '#fff',
          borderBottom: '1px solid #dee2e6',
          padding: '6px 12px',
          zIndex: 10,
          minHeight: 42,
        }}
      >
        <PcrBulkActionsToolbar
          selectedIds={Array.from(selectedIds)}
          onClearSelection={() => setSelectedIds(new Set())}
          onSuccess={(msg, toastType, options) => {
            if (options?.clearSelection !== false) {
              setSelectedIds(new Set());
            }
            onBulkSuccess(msg, toastType);
            onGroupChange();
          }}
          canClose={canClose}
          canAddNote={canAddNote}
          canLinkWorkId={canLinkWorkId}
        />
      </div>

      <TableScroll>
        <Table
          hover
          stickyHeader
          className="tw-mb-0"
          style={{ fontSize: '0.875rem', width: '100%' }}
        >
          <thead>
            <tr>
              <th />
              <SortableTh label="PCR ID" sortKey="reportId" activeKey={sortBy} direction={sortDir} onSort={handleSort} className="tw-font-semibold" />
              <SortableTh label="Received" sortKey="receivedAt" activeKey={sortBy} direction={sortDir} onSort={handleSort} className="tw-font-semibold" />
              <SortableTh label="Status" sortKey="status" activeKey={sortBy} direction={sortDir} onSort={handleSort} className="tw-font-semibold" />
              <SortableTh label="SLA" sortKey="sla" activeKey={sortBy} direction={sortDir} onSort={handleSort} className="tw-font-semibold" />
              <SortableTh label="Foreman area" sortKey="foremanArea" activeKey={sortBy} direction={sortDir} onSort={handleSort} className="tw-font-semibold" />
              <SortableTh label="Activity" sortKey="activity" activeKey={sortBy} direction={sortDir} onSort={handleSort} className="tw-font-semibold" />
              <SortableTh
                label="Similarity"
                sortKey="similarity"
                activeKey={sortBy}
                direction={sortDir}
                onSort={handleSort}
                className="tw-font-semibold"
              >
                {' '}
                <button
                  type="button"
                  className="tw-bg-transparent tw-border-0 tw-p-0 tw-text-content-muted"
                  style={{ fontSize: '0.75rem', verticalAlign: 'middle', lineHeight: 1 }}
                  onClick={(e) => { e.stopPropagation(); setShowSimilarityInfo(true); }}
                  title="How similarity works"
                >
                  <i className="bi bi-question-circle" />
                </button>
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {result.groups.map((group, groupIndex) => {
              const groupLabel = String.fromCharCode(65 + (groupIndex % 26));
              const memberIds = group.members.map((m) => m.id);
              const allSelected = memberIds.every((id) => selectedIds.has(id));
              const someSelected = memberIds.some((id) => selectedIds.has(id));
              const collapsed = collapsedGroups.has(group.groupKey);

              return (
                <React.Fragment key={group.groupKey}>
                  {/* Group header row */}
                  <tr
                    style={{
                      background: '#f6f8fa',
                      borderTop: '1px solid #dee2e6',
                      cursor: 'pointer',
                    }}
                    onClick={() => toggleCollapse(group.groupKey)}
                  >
                    <td className="tw-align-middle" style={{ paddingLeft: 10 }}>
                      <button
                        type="button"
                        className="tw-bg-transparent tw-border-0 tw-p-0 tw-text-content-secondary"
                        style={{ fontSize: '0.75rem', lineHeight: 1 }}
                        onClick={(e) => { e.stopPropagation(); toggleCollapse(group.groupKey); }}
                        title={collapsed ? 'Expand group' : 'Collapse group'}
                        aria-label={collapsed ? 'Expand group' : 'Collapse group'}
                      >
                        <i className={`bi bi-chevron-${collapsed ? 'right' : 'down'}`} />
                      </button>
                    </td>

                    {/* Road label + group name spans cols 2–6 */}
                    <td
                      colSpan={5}
                      className="tw-align-middle"
                      style={{ paddingLeft: 2 }}
                      onClick={(e) => { e.stopPropagation(); toggleGroupSelect(group); }}
                    >
                      <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                        <input
                          type="checkbox"
                          className="tw-h-4 tw-w-4 tw-shrink-0"
                          style={{ marginTop: 0 }}
                          checked={allSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = someSelected && !allSelected;
                          }}
                          onChange={() => toggleGroupSelect(group)}
                          onClick={(e) => e.stopPropagation()}
                          title="Select all in group"
                        />
                        <span className="tw-font-semibold" style={{ color: '#1f2328', whiteSpace: 'nowrap' }}>
                          Group {groupLabel}
                        </span>
                        <span className="tw-text-content-muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {group.label}
                        </span>
                      </div>
                    </td>

                    {/* N PCRs badge in Activity col */}
                    <td className="tw-align-middle tw-whitespace-nowrap">
                      <span
                        className="tw-inline-block tw-px-2 tw-py-1 tw-leading-none tw-rounded-full"
                        style={{
                          background: '#fff3cd',
                          color: '#996500',
                          border: '1px solid #ffc10740',
                          fontSize: '0.72rem',
                          fontWeight: 600,
                        }}
                      >
                        {group.members.length} PCRs
                      </span>
                    </td>

                    {/* Reason tags in Similarity col */}
                    <td className="tw-align-middle">
                      {group.reasonTags.map((tag) => (
                        <span
                          key={tag}
                          style={{
                            display: 'inline-block',
                            fontSize: '0.72rem',
                            color: '#dc3545',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </td>
                  </tr>

                  {/* Member rows */}
                  {!collapsed &&
                    sortMembers(group.members).map((member) => {
                      const removeKey = `${member.id}_${group.groupKey}`;
                      const isRemoving = removingKey === removeKey;
                      const isHovered = hoveredRowId === member.id;

                      return (
                        <tr
                          key={member.id}
                          style={{ background: selectedIds.has(member.id) ? '#f0f5ff' : undefined }}
                          onMouseEnter={() => setHoveredRowId(member.id)}
                          onMouseLeave={() => setHoveredRowId(null)}
                        >
                          {/* Indent checkbox */}
                          <td className="wrap-touch-check" style={{ paddingLeft: 28 }}>
                            <input
                              type="checkbox"
                              className="tw-h-4 tw-w-4"
                              checked={selectedIds.has(member.id)}
                              onChange={() => toggleSelect(member.id)}
                            />
                          </td>

                          {/* PCR ID */}
                          <td className="tw-whitespace-nowrap">
                            <button
                              type="button"
                              className="tw-bg-transparent tw-border-0 tw-p-0"
                              style={{ color: '#0d6efd', fontWeight: 500, fontSize: 'inherit', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                              onClick={() => onOpenDetail(member.id)}
                              title={member.reportId ?? `#${member.id}`}
                            >
                              {member.reportId ?? `#${member.id}`}
                            </button>
                          </td>

                          {/* Received */}
                          <td className="tw-whitespace-nowrap tw-text-content-muted tw-text-sm">
                            {formatReceived(member.receivedAt)}
                          </td>

                          {/* Status */}
                          <td>
                            <StatusBadge status={member.status} vocabulary="pcr" label={formatPcrStatusLabel(member.status)} />
                          </td>

                          {/* SLA */}
                          <td className="tw-text-sm tw-text-content-muted tw-whitespace-nowrap">
                            {member.responseTimeBucket || '–'}
                          </td>

                          {/* Foreman area (community) */}
                          <td
                            className="tw-text-sm tw-whitespace-nowrap"
                            title={
                              member.foremanAreaName ??
                              member.community ??
                              member.serviceArea ??
                              undefined
                            }
                          >
                            {member.foremanAreaName || member.community || member.serviceArea || '–'}
                          </td>

                          {/* Activity */}
                          <td
                            className="tw-text-sm tw-whitespace-nowrap"
                            title={member.activityType ?? undefined}
                          >
                            {shortActivity(member.activityType) ||
                              normalizePcrReportTypeBucket(member.reportType)}
                          </td>

                          {/* Similarity bar + remove-from-group button */}
                          <td>
                            <div className="tw-flex tw-items-center tw-gap-2">
                              <SimilarityBar score={member.similarityScore} />
                              {/* Revealed on row hover on desktop; always shown on touch, where
                                  there is no hover (W-iP13, see styles/interaction.css). */}
                              {(
                                <OverlayTrigger
                                  placement="left"
                                  overlay={
                                    <Tooltip>
                                      Remove from group – marks this pairing as incorrect and won&apos;t re-group these PCRs.
                                    </Tooltip>
                                  }
                                >
                                  <button
                                    type="button"
                                    className="wrap-hover-reveal wrap-touch-target tw-bg-transparent tw-border-0 tw-p-0 tw-text-content-muted"
                                    data-revealed={isHovered || isRemoving ? 'true' : 'false'}
                                    style={{ lineHeight: 1, fontSize: '0.85rem' }}
                                    onClick={() => void handleRemoveFromGroup(member.id, group)}
                                    disabled={isRemoving}
                                    aria-label="Remove from group"
                                  >
                                    {isRemoving ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <i className="bi bi-x-circle" />
                                    )}
                                  </button>
                                </OverlayTrigger>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </React.Fragment>
              );
            })}

            {/* Ungrouped section */}
            {result.ungrouped.length > 0 && (
              <>
                <tr style={{ background: '#f6f8fa', borderTop: '1px solid #dee2e6' }}>
                  <td
                    colSpan={COL_COUNT}
                    style={{
                      paddingTop: 8,
                      paddingBottom: 8,
                      paddingLeft: 12,
                    }}
                  >
                    <span
                      style={{
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        color: '#6c757d',
                      }}
                    >
                      Ungrouped
                      <span style={{ fontWeight: 400, marginLeft: 6 }}>
                        · {result.ungrouped.length} PCR{result.ungrouped.length !== 1 ? 's' : ''} with no similar matches
                      </span>
                    </span>
                  </td>
                </tr>
                {result.ungrouped.map((member) => (
                  <tr
                    key={member.id}
                    style={{ background: selectedIds.has(member.id) ? '#f0f5ff' : undefined }}
                  >
                    <td className="wrap-touch-check">
                      <input
                        type="checkbox"
                        className="tw-h-4 tw-w-4"
                        checked={selectedIds.has(member.id)}
                        onChange={() => toggleSelect(member.id)}
                      />
                    </td>
                    <td className="tw-whitespace-nowrap">
                      <button
                        type="button"
                        className="tw-bg-transparent tw-border-0 tw-p-0"
                        style={{ color: '#0d6efd', fontWeight: 500, fontSize: 'inherit' }}
                        onClick={() => onOpenDetail(member.id)}
                        title={member.reportId ?? `#${member.id}`}
                      >
                        {member.reportId ?? `#${member.id}`}
                      </button>
                    </td>
                    <td className="tw-whitespace-nowrap tw-text-content-muted tw-text-sm">{formatReceived(member.receivedAt)}</td>
                    <td>
                      <StatusBadge status={member.status} vocabulary="pcr" label={formatPcrStatusLabel(member.status)} />
                    </td>
                    <td className="tw-text-sm tw-text-content-muted tw-whitespace-nowrap">
                      {member.responseTimeBucket || '–'}
                    </td>
                    <td
                      className="tw-text-sm tw-whitespace-nowrap"
                      title={
                        member.foremanAreaName ??
                        member.community ??
                        member.serviceArea ??
                        undefined
                      }
                    >
                      {member.foremanAreaName || member.community || member.serviceArea || '–'}
                    </td>
                    <td
                      className="tw-text-sm tw-whitespace-nowrap"
                      title={member.activityType ?? undefined}
                    >
                      {shortActivity(member.activityType) ||
                        normalizePcrReportTypeBucket(member.reportType)}
                    </td>
                    <td className="tw-text-content-muted tw-text-sm">–</td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </Table>
      </TableScroll>

      {/* Bottom status bar */}
      <div
        className="tw-flex tw-items-center tw-shrink-0"
        style={{
          background: '#fff',
          borderTop: '1px solid #dee2e6',
          padding: '6px 12px',
          zIndex: 10,
        }}
      >
        <span
          className="tw-inline-block tw-px-2 tw-py-1 tw-leading-none tw-rounded-full tw-font-normal"
          style={{
            background: '#f0f0f0',
            color: '#555',
            border: '1px solid #ddd',
            fontSize: '0.8rem',
          }}
        >
          {totalMembers} total · {result.groups.length} group{result.groups.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Similarity info modal */}
      <Modal show={showSimilarityInfo} onHide={() => setShowSimilarityInfo(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="tw-text-base">How similarity works</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tw-text-sm">
          <p className="tw-text-content-muted">
            PCRs are grouped by a similarity score (0–100%) based on shared fields:
          </p>
          <Table size="sm" bordered className="tw-mb-3">
            <thead>
              <tr>
                <th>Signal</th>
                <th className="tw-text-right">Weight</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Same road name</td><td className="tw-text-right">35%</td></tr>
              <tr><td>Same activity type</td><td className="tw-text-right">20%</td></tr>
              <tr><td>Same service area</td><td className="tw-text-right">10%</td></tr>
              <tr><td>Within 14-day window</td><td className="tw-text-right">10%</td></tr>
              <tr><td>Same community</td><td className="tw-text-right">5%</td></tr>
              <tr><td>Same report type</td><td className="tw-text-right">5%</td></tr>
              <tr><td>Similar description (AI)</td><td className="tw-text-right">up to 30%</td></tr>
            </tbody>
          </Table>
          <p className="tw-text-content-muted tw-mb-0">
            PCRs scoring <strong>50% or above</strong> are grouped together. Hover a grouped
            row and click <i className="bi bi-x-circle" /> to remove it from a group – it won&apos;t
            be re-grouped with those PCRs again.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSimilarityInfo(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PcrGroupedTable;
